import React, { FunctionComponent } from 'react';

import { Text } from 'components/chroma';
import SummaryCard from 'components/SummaryCard';

import { toDisplayAddress } from 'scenes/common/helpers/address';
import {
  ApiData,
  BEDROOM_NUMBER_OPTIONS,
  BuildingType,
  BUILDING_LEVEL_OPTIONS,
} from 'scenes/common/types';
import { getApiData } from 'scenes/common/helpers/apiData';
import { toCurrencyWithSign } from 'scenes/common/helpers/currency';

import { LandlordFullPageProps } from 'scenes/landlord/components/FullPage/types';
import {
  anyFutureRenovationPlanned,
  certificateOfOccupancyAnswerRequired,
  getBuildingType,
  getClaimsMade,
  getMainAddress,
  getMortgageProvider,
  getMortgageProviderOther,
  getNumberOfBedrooms,
  getPropertyBuiltYear,
  getPurposeOfProperty,
  getRecentInsurer,
  getRoofMaterial,
  getSecurityFeatures,
  getUnitLevel,
  getWallMaterial,
  getWeeklyRentPaid,
  getYearsInsured,
  hasAdditionalSecurityInstalled,
  hasBeenRewiredOrReplumbed,
  hasBreakInClaims,
  hasBuildingCoverType,
  hasCertificateOfOccupancy,
  hasContentsCoverType,
  hasMoreThan3UnrelatedPeople,
  heritageListedAnswerRequired,
  isFreestandingAboveGround,
  isFreestandingMultiStorey,
  isHeritageListed,
  isOver5Acres,
  isPropertyInGoodCondition,
  isPropertyOwned,
  isPropertyUnderRenovation,
  isPropertyUnoccupied,
  isPropertyUsedForBusiness,
  isRentalAgreement,
  isTerraceMultiStorey,
  isTerraceUnderStrata,
  isTownhouseMultiStorey,
  isTownhouseUnderStrata,
  isUnitMultiStorey,
  isUnitWithContentsOnlyCover,
  rewiredOrReplumbedAnswerRequired,
} from 'scenes/landlord/selectors';
import {
  LandlordFunnelState,
  LandlordPolicySubSection,
  LandlordQuoteSubSection,
  ReferenceData,
} from 'scenes/landlord/types';

const getInsurerName = (
  funnelState: LandlordFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const insurerCode = getRecentInsurer(funnelState);
  return (
    Object.values(getApiData(referenceData).recentInsurerOptions)
      .flat()
      .find((insurer) => insurer.value === insurerCode)?.label ?? insurerCode
  );
};

const toClaimsMade = (funnelState: LandlordFunnelState): string => {
  const claimsMade = getClaimsMade(funnelState);
  if (claimsMade === 0) {
    return 'None';
  }
  return `${claimsMade} claim${claimsMade > 1 ? 's' : ''} made`;
};

const getBuildingTypeDescription = (
  funnelState: LandlordFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const buildingType = getBuildingType(funnelState);
  return (
    getApiData(referenceData).buildingTypes.find(
      (type) => type.value === buildingType
    )?.label ?? buildingType
  );
};

const getUnitLevelDescription = (funnelState: LandlordFunnelState) => {
  const unitLevel = getUnitLevel(funnelState);
  return (
    BUILDING_LEVEL_OPTIONS.find((type) => type.value === unitLevel)?.label ??
    unitLevel
  );
};

const getNumberOfBedroomsDescription = (funnelState: LandlordFunnelState) => {
  const numberOfBedrooms = getNumberOfBedrooms(funnelState);
  return (
    BEDROOM_NUMBER_OPTIONS.find((option) => option.value === numberOfBedrooms)
      ?.label ?? numberOfBedrooms
  );
};

const getRoofMaterialDescription = (
  funnelState: LandlordFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const roofMaterial = getRoofMaterial(funnelState);
  return (
    getApiData(referenceData).roofMaterials.find(
      (type) => type.value === roofMaterial
    )?.label ?? roofMaterial
  );
};

const getWallMaterialDescription = (
  funnelState: LandlordFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const wallMaterial = getWallMaterial(funnelState);
  return (
    getApiData(referenceData).wallMaterials.find(
      (type) => type.value === wallMaterial
    )?.label ?? wallMaterial
  );
};

const getSecurityFeatureDescription = (
  securityFeature: string,
  referenceData: ApiData<ReferenceData>
) => {
  const homeSecurity = getApiData(referenceData).homeSecurities.find(
    (type) => type.value === securityFeature
  );
  return homeSecurity?.value === 'NoAlarm'
    ? 'None'
    : homeSecurity?.label ?? securityFeature;
};

const toConstructionRenovationOrDemolition = (
  funnelState: LandlordFunnelState
): string => {
  if (isPropertyUnderRenovation(funnelState)) {
    return 'Undergoing';
  }
  return anyFutureRenovationPlanned(funnelState) ? 'Planned' : 'No';
};

const getMortgageDescription = (
  funnelState: LandlordFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const mortgageProvider = getMortgageProvider(funnelState);
  if (!mortgageProvider) {
    return 'None';
  }
  if (mortgageProvider === 'Other') {
    return getMortgageProviderOther(funnelState);
  }
  return (
    getApiData(referenceData).mortgageProviderOptions.find(
      (type) => type.value === mortgageProvider
    )?.label || mortgageProvider
  );
};

const getPropertyBuiltYearDescription = (
  funnelState: LandlordFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const builtYear = getPropertyBuiltYear(funnelState);
  return (
    getApiData(referenceData).buildYears.find(
      ({ value }) => value === builtYear
    )?.label ?? builtYear
  );
};

const BuildingTypeComponent: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState'
>> = ({ funnelState }) => {
  switch (getBuildingType(funnelState)) {
    case BuildingType.FreestandingHouse:
      return (
        <>
          <Text>
            Has gap of a metre or more?{' '}
            {isFreestandingAboveGround(funnelState) ? 'Yes' : 'No'}
          </Text>
          <Text>
            Has more than one storey?{' '}
            {isFreestandingMultiStorey(funnelState) ? 'Yes' : 'No'}
          </Text>
        </>
      );
    case BuildingType.Terrace:
      return (
        <>
          <Text>
            Has more than one storey?{' '}
            {isTerraceMultiStorey(funnelState) ? 'Yes' : 'No'}
          </Text>
          {hasBuildingCoverType(funnelState) && (
            <Text>
              Looked after by a body corporate or strata manager?{' '}
              {isTerraceUnderStrata(funnelState) ? 'Yes' : 'No'}
            </Text>
          )}
        </>
      );
    case BuildingType.Townhouse:
      return (
        <>
          <Text>
            Has more than one storey?{' '}
            {isTownhouseMultiStorey(funnelState) ? 'Yes' : 'No'}
          </Text>
          {hasBuildingCoverType(funnelState) && (
            <Text>
              Looked after by a body corporate or strata manager?{' '}
              {isTownhouseUnderStrata(funnelState) ? 'Yes' : 'No'}
            </Text>
          )}
        </>
      );
    case BuildingType.Unit:
      return (
        <>
          <Text>
            Has more than one storey?{' '}
            {isUnitMultiStorey(funnelState) ? 'Yes' : 'No'}
          </Text>
          <Text>Unit level? {getUnitLevelDescription(funnelState)}</Text>
          {hasBuildingCoverType(funnelState) && (
            <Text>
              Looked after by a body corporate or strata manager?{' '}
              {isTownhouseUnderStrata(funnelState) ? 'Yes' : 'No'}
            </Text>
          )}
        </>
      );
    default:
      return <></>;
  }
};

export const PropertySummary: FunctionComponent<Pick<
  LandlordFullPageProps,
  'funnelState' | 'referenceData' | 'onEditQuoteDetails' | 'onEditPolicyDetails'
>> = ({
  funnelState,
  referenceData,
  onEditQuoteDetails,
  onEditPolicyDetails,
}) => (
  <>
    <SummaryCard
      heading="Property address"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.MainAddress)
      }
    >
      <Text>{toDisplayAddress(getMainAddress(funnelState))}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Own the property?"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.PropertyOwnership)
      }
    >
      <Text>{isPropertyOwned(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>
    <SummaryCard
      heading="How the property is used"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.PropertyPurpose)
      }
    >
      <Text>{getPurposeOfProperty(funnelState)}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Rental agreement with tenant"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.RentalAgreement)
      }
    >
      <Text>{isRentalAgreement(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Weekly rent paid"
      onEditClick={() => onEditQuoteDetails(LandlordQuoteSubSection.WeeklyRent)}
    >
      <Text>{toCurrencyWithSign(getWeeklyRentPaid(funnelState))}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Used for business"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.UsedForBusiness)
      }
    >
      <Text>{isPropertyUsedForBusiness(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>

    <SummaryCard
      heading="Recent Insurer"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.RecentInsurer)
      }
    >
      <Text>{getInsurerName(funnelState, referenceData)}</Text>
    </SummaryCard>
    <SummaryCard
      heading="Years insured"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.YearsInsured)
      }
    >
      <Text>{`${getYearsInsured(funnelState)} year${
        getYearsInsured(funnelState) === 1 ? '' : 's'
      }`}</Text>
    </SummaryCard>

    <SummaryCard
      heading="Claims made in the last 3 years"
      onEditClick={() => onEditQuoteDetails(LandlordQuoteSubSection.ClaimsMade)}
    >
      <Text>{toClaimsMade(funnelState)}</Text>
    </SummaryCard>

    {getClaimsMade(funnelState) > 0 && (
      <SummaryCard
        heading="Any claims related to break in?"
        onEditClick={() => onEditQuoteDetails(LandlordQuoteSubSection.BreakIn)}
      >
        <Text>{hasBreakInClaims(funnelState) ? 'Yes' : 'No'}</Text>
      </SummaryCard>
    )}

    {hasBreakInClaims(funnelState) && (
      <SummaryCard
        heading="Additional security installed?"
        onEditClick={() =>
          onEditQuoteDetails(LandlordQuoteSubSection.AdditionalSecurity)
        }
      >
        <Text>
          {hasAdditionalSecurityInstalled(funnelState) ? 'Yes' : 'No'}
        </Text>
      </SummaryCard>
    )}

    <SummaryCard
      heading="Building type"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.BuildingType)
      }
    >
      <Text>{getBuildingTypeDescription(funnelState, referenceData)}</Text>
      <BuildingTypeComponent funnelState={funnelState} />
    </SummaryCard>

    <SummaryCard
      heading="Number of bedrooms"
      onEditClick={() =>
        onEditQuoteDetails(LandlordQuoteSubSection.NumberOfBedrooms)
      }
    >
      <Text>
        {getNumberOfBedroomsDescription(funnelState)} bedroom
        {getNumberOfBedroomsDescription(funnelState) === '1' ? '' : 's'}
      </Text>
    </SummaryCard>
    {!isUnitWithContentsOnlyCover(funnelState) && (
      <SummaryCard
        heading="Year the property was built"
        onEditClick={() =>
          onEditQuoteDetails(LandlordQuoteSubSection.PropertyBuiltYear)
        }
      >
        <Text>
          {getPropertyBuiltYearDescription(funnelState, referenceData)}
        </Text>
      </SummaryCard>
    )}
    {certificateOfOccupancyAnswerRequired(
      getPropertyBuiltYear(funnelState)
    ) && (
      <SummaryCard
        heading="Has certificate of occupancy?"
        onEditClick={() =>
          onEditQuoteDetails(LandlordQuoteSubSection.CertificateOfOccupancy)
        }
      >
        <Text>{hasCertificateOfOccupancy(funnelState) ? 'Yes' : 'No'}</Text>
      </SummaryCard>
    )}
    {heritageListedAnswerRequired(getPropertyBuiltYear(funnelState)) && (
      <SummaryCard
        heading="Heritage listed?"
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.HeritageListed)
        }
      >
        <Text>{isHeritageListed(funnelState) ? 'Yes' : 'No'}</Text>
      </SummaryCard>
    )}
    {rewiredOrReplumbedAnswerRequired(getPropertyBuiltYear(funnelState)) && (
      <SummaryCard
        heading="Rewired or replumbed?"
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.ReplumbedOrRewired)
        }
      >
        <Text>{hasBeenRewiredOrReplumbed(funnelState) ? 'Yes' : 'No'}</Text>
      </SummaryCard>
    )}
    {!isUnitWithContentsOnlyCover(funnelState) && (
      <>
        <SummaryCard
          heading="Roof material"
          onEditClick={() =>
            onEditQuoteDetails(LandlordQuoteSubSection.RoofMaterial)
          }
        >
          <Text>{getRoofMaterialDescription(funnelState, referenceData)}</Text>
        </SummaryCard>
        <SummaryCard
          heading="Wall material"
          onEditClick={() =>
            onEditQuoteDetails(LandlordQuoteSubSection.WallMaterial)
          }
        >
          <Text>{getWallMaterialDescription(funnelState, referenceData)}</Text>
        </SummaryCard>
      </>
    )}

    {hasContentsCoverType(funnelState) && (
      <SummaryCard
        heading="Security features"
        onEditClick={() =>
          onEditQuoteDetails(LandlordQuoteSubSection.SecurityFeatures)
        }
      >
        {getSecurityFeatures(funnelState).map((securityFeature) => (
          <Text key={securityFeature}>
            {getSecurityFeatureDescription(securityFeature, referenceData)}
          </Text>
        ))}
      </SummaryCard>
    )}

    <SummaryCard
      heading="Property in good condition?"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.ProperyInGoodCondition)
      }
    >
      <Text>{isPropertyInGoodCondition(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>

    <SummaryCard
      heading="Construction, renovation or demolition?"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.PropertyUnderRenovation)
      }
    >
      <Text>{toConstructionRenovationOrDemolition(funnelState)}</Text>
    </SummaryCard>

    <SummaryCard
      heading="Property unoccupied for more than 60 days?"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.PropertyUnoccupied)
      }
    >
      <Text>{isPropertyUnoccupied(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>
    <SummaryCard
      heading="More than 5 acres of land?"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.PropertyOver5Acres)
      }
    >
      <Text>{isOver5Acres(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>
    <SummaryCard
      heading="3 or more unrelated people?"
      onEditClick={() =>
        onEditPolicyDetails(LandlordPolicySubSection.PropertyUnrelatedPeople)
      }
    >
      <Text>{hasMoreThan3UnrelatedPeople(funnelState) ? 'Yes' : 'No'}</Text>
    </SummaryCard>

    {hasBuildingCoverType(funnelState) && (
      <SummaryCard
        heading="Mortgage with?"
        onEditClick={() =>
          onEditPolicyDetails(LandlordPolicySubSection.Mortgage)
        }
      >
        <Text>{getMortgageDescription(funnelState, referenceData)}</Text>
      </SummaryCard>
    )}
  </>
);

export default PropertySummary;
